"use client";

import { useEffect, useRef } from "react";

const routes = [
    {
        origin: "Dushanbe, Tajikistan",
        destination: "Rotterdam, NL",
        transit: "28–35 days",
        incoterm: "CIF",
        mode: "Rail → Sea",
    },
    {
        origin: "Johannesburg, ZA",
        destination: "Antwerp, BE",
        transit: "22–28 days",
        incoterm: "CIF",
        mode: "Road → Sea",
    },
    {
        origin: "Johannesburg, ZA",
        destination: "Shanghai, CN",
        transit: "18–22 days",
        incoterm: "CFR",
        mode: "Road → Sea",
    },
    {
        origin: "Rotterdam, NL",
        destination: "Customer Plant",
        transit: "2–5 days",
        incoterm: "DAP",
        mode: "Road (Bonded)",
    },
];

export default function LogisticsSection() {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const els = ref?.current?.querySelectorAll(".reveal");
        if (!els) return;
        const observer = new IntersectionObserver(
            (entries) =>
                entries.forEach((e) => {
                    if (e.isIntersecting) {
                        e.target.classList.add("visible");
                        observer.unobserve(e.target);
                    }
                }),
            { threshold: 0.1 }
        );
        els?.forEach((el) => observer?.observe(el));
        return () => observer?.disconnect();
    }, []);

    return (
        <section
            ref={ref}
            className="section-navy-mid"
            style={{ padding: "120px 0" }}
            aria-labelledby="logistics-heading"
        >
            <div
                className="px-6 lg:px-12"
                style={{ maxWidth: "1280px", margin: "0 auto" }}
            >
                {/* Header */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
                    <div>
                        <span className="type-subheading reveal">Logistics</span>
                        <h2
                            id="logistics-heading"
                            className="reveal reveal-delay-1"
                            style={{
                                fontFamily: "var(--font-serif)",
                                fontSize: "clamp(2rem, 3.5vw, 3rem)",
                                fontWeight: 400,
                                color: "var(--color-white)",
                                lineHeight: 1.1,
                                letterSpacing: "-0.02em",
                                marginTop: "12px",
                            }}
                        >
                            Bonded stock at three
                            <br />
                            major port terminals.
                        </h2>
                    </div>
                    <div className="flex items-center">
                        <p
                            className="reveal reveal-delay-2"
                            style={{
                                fontFamily: "var(--font-sans)",
                                fontSize: "1rem",
                                color: "rgba(255,255,255,0.55)",
                                lineHeight: "1.7",
                            }}
                        >
                            We manage our own bonded warehouse positions at Rotterdam,
                            Antwerp, and Durban. This eliminates origin-to-plant transit
                            risk and enables rapid drawdown against confirmed orders.
                        </p>
                    </div>
                </div>

                {/* Route table */}
                <div
                    className="reveal reveal-delay-2"
                    style={{ overflowX: "auto" }}
                >
                    <table
                        className="data-table"
                        style={{ background: "transparent" }}
                        aria-label="Logistics routes and transit times"
                    >
                        <thead>
                            <tr>
                                {["Origin", "Destination", "Transit Time", "Incoterm", "Mode"]?.map(
                                    (h) => (
                                        <th
                                            key={h}
                                            style={{
                                                background: "rgba(255,255,255,0.04)",
                                                color: "rgba(255,255,255,0.35)",
                                                borderBottom: "1px solid rgba(255,255,255,0.08)",
                                            }}
                                        >
                                            {h}
                                        </th>
                                    )
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {routes?.map((r, i) => (
                                <tr key={i}>
                                    {[r?.origin, r?.destination, r?.transit, r?.incoterm, r?.mode]?.map(
                                        (cell, j) => (
                                            <td
                                                key={j}
                                                style={{
                                                    color:
                                                        j === 3
                                                            ? "var(--color-red)"
                                                            : "rgba(255,255,255,0.65)",
                                                    borderBottom: "1px solid rgba(255,255,255,0.06)",
                                                    fontWeight: j === 3 ? 600 : 400,
                                                    fontSize: "0.875rem",
                                                }}
                                            >
                                                {cell}
                                            </td>
                                        )
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Warehouse stat cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
                    {[
                        {
                            port: "Rotterdam",
                            flag: "🇳🇱",
                            capacity: "1,200 t",
                            note: "Primary European hub. Bonded under Dutch Customs Warehousing.",
                        },
                        {
                            port: "Antwerp",
                            flag: "🇧🇪",
                            capacity: "800 t",
                            note: "Secondary EU hub. Integrated with rail connections to Central Europe.",
                        },
                        {
                            port: "Durban",
                            flag: "🇿🇦",
                            capacity: "600 t",
                            note: "African origin hub. Direct pipeline from Johannesburg smelter.",
                        },
                    ]?.map((w, i) => (
                        <div
                            key={w?.port}
                            className={`reveal reveal-delay-${i + 1}`}
                            style={{
                                background: "rgba(255,255,255,0.04)",
                                border: "1px solid rgba(255,255,255,0.08)",
                                borderRadius: "4px",
                                padding: "28px",
                            }}
                        >
                            <p
                                style={{
                                    fontSize: "1.5rem",
                                    marginBottom: "8px",
                                }}
                                aria-hidden="true"
                            >
                                {w?.flag}
                            </p>
                            <p
                                style={{
                                    fontFamily: "var(--font-sans)",
                                    fontSize: "0.875rem",
                                    fontWeight: 600,
                                    color: "rgba(255,255,255,0.85)",
                                    marginBottom: "4px",
                                }}
                            >
                                {w?.port}
                            </p>
                            <p
                                style={{
                                    fontFamily: "var(--font-serif)",
                                    fontSize: "1.75rem",
                                    color: "var(--color-red)",
                                    lineHeight: 1,
                                    marginBottom: "10px",
                                }}
                            >
                                {w?.capacity}
                            </p>
                            <p
                                style={{
                                    fontFamily: "var(--font-sans)",
                                    fontSize: "0.8125rem",
                                    color: "rgba(255,255,255,0.38)",
                                    lineHeight: "1.55",
                                }}
                            >
                                {w?.note}
                            </p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}